import { useState } from "react";
import { Link } from "react-router-dom";
import ActivityTab           from "./ActivityTab";
import FinanceTab            from "./FinanceTab";
import DeletionRequestsTab   from "./DeletionRequestsTab";
import s from "./ReportsPage.module.css";

/** Top-level admin reports landing. Tabs are organised by *concern* (what the
 *  data is about) rather than *stakeholder*, because most events cross roles
 *  (a grade entry is teacher-and-student; a book purchase is library-and-
 *  money). A stakeholder axis would force every row into a best-fit home. */
const TABS = [
  { key: "activity",  label: "Activity Log",
    hint: "Audit trail of everything that happens in the system." },
  { key: "finance",   label: "Finance",
    hint: "Money coming in (tuition, books) vs going out (salaries)." },
  { key: "deletions", label: "Deletion Requests",
    hint: "Cashier-requested payment deletions — pending, approved, and rejected. Approved rows keep their snapshot so the audit trail survives the deletion." },
  { key: "academics", label: "Academics",
    hint: "Teacher loads, enrolment numbers, subject coverage." },
  { key: "library",   label: "Library",
    hint: "Book inventory and purchase activity." },
];

export default function ReportsPage() {
  const [tab, setTab] = useState("activity");
  const active = TABS.find(t => t.key === tab);

  return (
    <div className={s.wrap}>
      <header className={s.header}>
        <div className={s.eyebrow}>ADMIN · REPORTS</div>
        <h1 className={s.title}>Reports</h1>
        <p className={s.sub}>
          Audit trail, money flow, and usage across the school. Everything here
          is read-only.
        </p>
      </header>

      <div className={s.tabBar} role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={t.key === tab}
            className={`${s.tab} ${t.key === tab ? s.tabActive : ""}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <p className={s.tabHint}>{active?.hint}</p>

      <div className={s.tabBody}>
        {tab === "activity"  && <ActivityTab />}
        {tab === "finance"   && <FinanceTab  />}
        {tab === "deletions" && <DeletionRequestsTab />}
        {tab === "academics" && <AcademicsTabPlaceholder />}
        {tab === "library"   && <LibraryTabPlaceholder   />}
      </div>
    </div>
  );
}

/** Placeholder — for now the Academics overview lives on the Admin Dashboard
 *  (Teaching Load + Subject Coverage panels). A more detailed report can go
 *  here once the shape of what's needed is clearer. */
function AcademicsTabPlaceholder() {
  return (
    <div className={s.placeholder}>
      <div className={s.placeholderTitle}>Coverage lives on the Dashboard</div>
      <p className={s.placeholderBody}>
        Teaching Load, Subject Coverage, and gap counts are already surfaced
        on the Admin Dashboard and refresh live. Deeper academics reports
        (enrolment trends, grade distributions, pass rates) will appear here
        as the data is collected.
      </p>
      <Link to="/dashboard" className={s.placeholderLink}>Open the Admin Dashboard →</Link>
    </div>
  );
}

function LibraryTabPlaceholder() {
  return (
    <div className={s.placeholder}>
      <div className={s.placeholderTitle}>Library reports — coming soon</div>
      <p className={s.placeholderBody}>
        Book inventory and required-book coverage will appear here. Book
        purchases are already counted in the Finance tab.
      </p>
      <Link to="/admin/books" className={s.placeholderLink}>Manage books →</Link>
    </div>
  );
}
